import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack } from '@mui/material'
import React, { useMemo } from 'react'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { WallboxModel } from 'src/Libs/e-mobility/wallboxes/models/wallbox-model'
import { ChargingCardToWallboxLinkRowItem } from './ChargingCardToWallboxLinkRowItem'
import { ChargingCardToWallboxFormData } from '../charging-card-to-wallbox-form-data'
import { Control, UseFormReset } from 'react-hook-form'
import { nullsFirst } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { SearchField } from '@e3dc-react/shell/Elements/InputFields'
import { ManagedModeInfoAlert } from '../ManagedModeInfoAlert'
import { ChargingCardModel } from '../../../api/models/charging-card-model'

interface ChargingToWallboxMobileViewViewProps {
  wallboxes: WallboxModel[]
  control: Control<ChargingCardToWallboxFormData>
  reset: UseFormReset<ChargingCardToWallboxFormData>
  onSearchChange: (param: string) => void
  search: string
  dirtyOCPITokenId: string | undefined
  disabledOCPITokenIds: string[]
  isAssignTokenLoading: boolean
  chargingCards: ChargingCardModel[]
  onSubmit: () => void
  onCancelButtonClick: () => void
  installationId: number
  isManagedMode: boolean
}

/**
 * Component for displaying table on mobile and tablet screens inside charging card to link wallboxes dialog
 */
export const ChargingToWallboxMobileView: React.FC<ChargingToWallboxMobileViewViewProps> = ({
  onSearchChange,
  wallboxes,
  reset,
  control,
  search,
  chargingCards,
  dirtyOCPITokenId,
  disabledOCPITokenIds,
  isAssignTokenLoading,
  onSubmit,
  onCancelButtonClick,
  installationId,
  isManagedMode,
}) => {
  const { t } = useCustomTranslation()

  const header = useMemo<Header<ChargingCardModel>[]>(() => {
    return [
      {
        value: 'ocpiTokenId',
        label: t('emobility.header.chargingCards'),
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
        isSortable: false,
        valueRender: (_, __, item: ChargingCardModel | undefined) => {
          if (!item) return <></>

          const isCurrentRowDirty = dirtyOCPITokenId === item.ocpiTokenId
          const shouldDisableCurrentRow = disabledOCPITokenIds.includes(item.ocpiTokenId)

          return (
            <ChargingCardToWallboxLinkRowItem
              cardName={item.cardName}
              cardStatus={item.status}
              cardUser={item.user}
              ocpiTokenId={item.ocpiTokenId}
              wallboxes={wallboxes}
              control={control}
              shouldDisableRow={shouldDisableCurrentRow}
              shouldButtonsBeVisible={isCurrentRowDirty}
              shouldDisableButtons={isAssignTokenLoading}
              handleSubmit={onSubmit}
              handleCancelButtonClick={onCancelButtonClick}
              installationId={installationId}
            />
          )
        },
      },
    ]
  }, [control, dirtyOCPITokenId, disabledOCPITokenIds, onSubmit, onCancelButtonClick, isAssignTokenLoading, t, wallboxes, installationId])

  return (
    <Stack height={600}>
      <Stack height="95%" sx={{ overflowY: 'scroll' }}>
        <ManagedModeInfoAlert
          isManagedMode={isManagedMode}
          reset={reset}
          chargingCards={chargingCards}
          wallboxes={wallboxes}
          installationId={installationId}
        />
        <TableComplete
          header={header}
          data={chargingCards}
          bodyElevation={0}
          footerElevation={0}
          headerElevation={0}
          coloredColumn
          disablePagination
          rowId={row => row.ocpiTokenId}
        />
      </Stack>
      <Stack mt={4}>
        <SearchField value={search} label={t('emobility.chargingCards.search-card-text')} name="search" onChange={onSearchChange} delayInMs={300} />
      </Stack>
    </Stack>
  )
}
